/**
 * SIG-P1 — P&L has THREE states, and collapsing any two of them is a lie.
 *
 *   known, non-zero  → a computed figure, coloured by sign
 *   known, zero      → genuinely FLAT. Neutral, never green: "+$0.00" in
 *                      profit colour is a claim that the position is doing
 *                      fine, and green is the reading that stops a trader
 *                      looking closer.
 *   unknown          → we could not compute it (no current price, unknown
 *                      side, unsupported symbol). Render an em-dash. It is
 *                      not a number and must never be printed as one.
 *
 * This existed as three copy-pasted coercions in trades-table.tsx:
 *
 *     typeof trade.pnl === "number" ? trade.pnl : parseFloat(String(trade.pnl)) || 0
 *
 * The row model maps a missing value to the honest "-" em-dash, and every one
 * of those sites turned it straight back into a number: parseFloat("-") is
 * NaN, and `NaN || 0` is 0 — so "unknown" rendered as a green +$0.00. The
 * backend half is fixed too (positions.py no longer collapses Decimal(0) and
 * None into the same null); this is the client half, single-sourced so a
 * fourth render site cannot reintroduce the coercion by copy-paste.
 */

export type PnlReading =
  | { known: true; value: number }
  | { known: false; value: null };

const UNKNOWN: PnlReading = { known: false, value: null };

/**
 * Read a raw P&L off a row model without inventing a value.
 *
 * Accepts what the table actually holds: a number, a numeric string, the "-"
 * em-dash placeholder, null/undefined, or junk. Anything that is not a finite
 * number reads as UNKNOWN — deliberately including the empty string, which
 * `parseFloat` would also reject but `Number("")` would happily call 0.
 */
export function readPnl(raw: unknown): PnlReading {
  if (typeof raw === "number") {
    return Number.isFinite(raw) ? { known: true, value: raw } : UNKNOWN;
  }
  if (typeof raw !== "string") return UNKNOWN;

  const trimmed = raw.trim();
  if (trimmed === "") return UNKNOWN;

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? { known: true, value: parsed } : UNKNOWN;
}

/**
 * The tone a P&L should be rendered in.
 *
 * "flat" is its own tone on purpose: a real zero is a fact worth showing, but
 * it is not a gain, so it must not borrow the profit colour.
 */
export type PnlTone = "up" | "down" | "flat" | "unknown";

export function pnlTone(reading: PnlReading): PnlTone {
  if (!reading.known) return "unknown";
  if (reading.value > 0) return "up";
  if (reading.value < 0) return "down";
  return "flat";
}

/**
 * Format a P&L for display. Unknown returns the em-dash — callers must render
 * this string rather than reaching for the raw number, which is what kept
 * regenerating the bug.
 */
export function formatPnl(reading: PnlReading): string {
  if (!reading.known) return "—";
  const magnitude = Math.abs(reading.value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  if (reading.value === 0) return `$${magnitude}`;
  return `${reading.value > 0 ? "+" : "-"}$${magnitude}`;
}
